import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  //  Grid, // REMOVIDO: Eliminando o componente Grid
  Paper,
  Pagination,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";

// O endpoint mudou de /winners para /lineups
const API_BASE_URL = "https://api.mercadoterra.com.br/admin/scale-competitions";

interface LineupUser {
  id: string;
  name: string;
  email: string;
}
interface Lineup {
  id: string;
  status: string;
  totalScore: number;
  ranking: number;
  createdAt: string;
  user: LineupUser;
  selectionsCount: number;
}
interface LineupStats {
  totalLineups: number;
  confirmed: number;
  totalRevenue: number;
  participationPrice: number;
  // Os campos 'pending' e 'cancelled' também podem existir na resposta real
}
interface LineupsList {
  lineups: Lineup[];
  total: number;
  page: number;
  limit: number;
}
interface LineupsResponse {
  // A estrutura da resposta é mantida
  lineups: LineupsList;
  stats: LineupStats;
  page: number; // Estes 'page' e 'limit' externos parecem redundantes mas podem vir na API
  limit: number;
}

interface SelectionDetail {
  player: { id: string; name: string; imageUrl: string };
  position: { id: string; name: string; pointWeight: number };
  selectionOrder: number;
}
interface LineupDetails {
  id: string;
  status: string;
  totalScore: number;
  ranking: number;
  user: LineupUser;
  selections: SelectionDetail[];
  createdAt: string;
}

interface CompetitionLineupsTabProps {
  token: string;
  competitionId: string;
  formatCurrency: (price: string) => string;
}

const CompetitionLineupsTab: React.FC<CompetitionLineupsTabProps> = ({
  token,
  competitionId,
  formatCurrency,
}) => {
  const [data, setData] = useState<LineupsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedLineup, setSelectedLineup] = useState<LineupDetails | null>(
    null
  );
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(20);
  const [filterStatus] = useState("CONFIRMED");

  useEffect(() => {
    const fetchLineups = async () => {
      setIsLoading(true);
      setError(null);

      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: limit.toString(),
        status: filterStatus,
      }).toString();

      try {
        const response = await fetch(
          `${API_BASE_URL}/${competitionId}/lineups?${queryParams}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) {
          throw new Error("Falha ao carregar escalações.");
        }
        setData(await response.json());
      } catch (err: any) {
        console.error(err);
        setError(err.message || "Erro ao buscar escalações.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchLineups();
  }, [token, competitionId, currentPage, limit, filterStatus]);

  // Função para mudar de página (inalterada)
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const fetchLineupDetails = async (lineupId: string) => {
    setIsDetailLoading(true);
    setSelectedLineup(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/lineups/${lineupId}/details`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error("Falha ao carregar detalhes da escalação.");
      }
      setSelectedLineup(await response.json());
    } catch (err: any) {
      console.error(err);
      alert(err.message || "Erro ao buscar detalhes da escalação.");
    } finally {
      setIsDetailLoading(false);
    }
  };

  const getStatusChipProps = (status: string) => {
    switch (status) {
      case "CONFIRMED":
        return { label: "CONFIRMADA", color: "success" as const };
      case "PENDING":
        return { label: "PENDENTE", color: "warning" as const };
      case "CANCELLED":
        return { label: "CANCELADA", color: "error" as const };
      default:
        return { label: status, color: "default" as const };
    }
  };

  // --- Renderização Condicional ---
  if (isLoading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />{" "}
      </Box>
    );
  if (error) return <Alert severity="error">{error}</Alert>;

  if (!data || !data.stats || !data.lineups)
    return (
      <Alert severity="info">Nenhuma informação de escalação disponível.</Alert>
    );

  // Variáveis seguras
  const stats = data.stats;
  const lineups = data.lineups.lineups;
  const totalLineups = data.lineups.total;
  const totalPages = Math.ceil(totalLineups / data.lineups.limit);

  return (
    <Box>
      {/* Estatísticas - SUBSTITUINDO GRID */}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 3,
          bgcolor: "primary.light",
          color: "primary.contrastText",
          // Flexbox para o layout das estatísticas
          display: "flex",
          flexWrap: "wrap",
          gap: 2, // Simula spacing={2}
        }}
      >
        {/* Item de Estatística (xs=6, sm=3) */}
        {/* Usamos width responsivo para simular xs={6} (50%) e sm={3} (25%) */}
        <Box sx={{ width: { xs: "calc(50% - 8px)", sm: "calc(25% - 8px)" } }}>
          <Typography variant="subtitle2">Total de Escalações:</Typography>
          <Typography variant="h6">{stats.totalLineups}</Typography>
        </Box>
        <Box sx={{ width: { xs: "calc(50% - 8px)", sm: "calc(25% - 8px)" } }}>
          <Typography variant="subtitle2">Confirmadas:</Typography>
          <Typography variant="h6">{stats.confirmed}</Typography>
        </Box>
        <Box sx={{ width: { xs: "calc(50% - 8px)", sm: "calc(25% - 8px)" } }}>
          <Typography variant="subtitle2">Preço de Participação:</Typography>
          <Typography variant="h6">
            {" "}
            {formatCurrency((+stats.participationPrice).toFixed(2))}{" "}
          </Typography>
        </Box>
        <Box sx={{ width: { xs: "calc(50% - 8px)", sm: "calc(25% - 8px)" } }}>
          <Typography variant="subtitle2">Receita Total:</Typography>
          <Typography variant="h6">
            {formatCurrency((+stats.totalRevenue).toFixed(2))}{" "}
          </Typography>
        </Box>
      </Paper>

      <Typography variant="h6" gutterBottom>
        Lista de Escalações Confirmadas ({totalLineups})
      </Typography>

      {/* Tratamento caso a lista de 'lineups' venha vazia */}
      {lineups.length === 0 && stats.totalLineups === 0 ? (
        <Alert severity="info">
          Não há escalações confirmadas para exibir nesta competição.
        </Alert>
      ) : (
        <>
          <List component={Paper} elevation={2}>
            {lineups.map((lineup) => (
              <React.Fragment key={lineup.id}>
                <ListItem
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="details"
                      onClick={() => fetchLineupDetails(lineup.id)}
                      disabled={isDetailLoading}
                    >
                      {isDetailLoading && selectedLineup?.id !== lineup.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={`${lineup.user.name} (Ranking: ${lineup.ranking}º)`}
                    secondary={`Score: ${lineup.totalScore} | Seleções: ${
                      lineup.selectionsCount
                    } | Status: ${getStatusChipProps(lineup.status).label}`}
                  />
                  <Box sx={{ ml: 2 }}>
                    <Chip {...getStatusChipProps(lineup.status)} size="small" />
                  </Box>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>

          {/* ⭐️ Componente de Paginação (inalterado) */}
          {totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 3,
                mb: 3,
              }}
            >
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      {/* Modal de Detalhes da Escalação (ajustado internamente) */}
      <Dialog
        open={!!selectedLineup}
        onClose={() => setSelectedLineup(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Detalhes da Escalação de {selectedLineup?.user.name}
          <IconButton
            onClick={() => setSelectedLineup(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isDetailLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : selectedLineup ? (
            <Box>
              {/* Score Total e Ranking - SUBSTITUINDO GRID */}
              <Box sx={{ display: "flex", gap: 2 }}>
                {/* Score Total (xs=6) */}
                <Box sx={{ flexGrow: 1, width: "50%" }}>
                  <Typography variant="subtitle1">Score Total:</Typography>
                  <Typography variant="h5" color="primary">
                    {selectedLineup.totalScore}
                  </Typography>
                </Box>
                {/* Ranking (xs=6) */}
                <Box sx={{ flexGrow: 1, width: "50%" }}>
                  <Typography variant="subtitle1">Ranking:</Typography>
                  <Typography variant="h5">
                    {selectedLineup.ranking}º
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Seleções ({selectedLineup.selections.length})
              </Typography>
              <List dense>
                {selectedLineup.selections.map((s, index) => (
                  <ListItem
                    key={index}
                    sx={{ borderBottom: "1px dotted #eee" }}
                  >
                    <ListItemText
                      primary={s.player.name}
                      secondary={`Posição: ${s.position.name} | Peso do Ponto: ${s.position.pointWeight}`}
                    />
                    <Chip label={`Ordem: ${s.selectionOrder}`} size="small" />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Alert severity="warning">Detalhes não carregados.</Alert>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CompetitionLineupsTab;
